/**
 * @module data/browserStore
 * @description Browser-storage implementations of the repositories.
 *
 * // TEMPORARY: demo persistence for the client preview.
 * What caused this: the Zoom demo must run before AFJ's IT supplies hosting
 * or Neon CRM API credentials. Why chosen: it implements the same interfaces
 * the production API adapter will, so nothing above this layer changes.
 * Side-effects: data lives only in the current browser profile. Other
 * devices do not see it, but other TABS do (via the `storage` event), which
 * lets the ballroom screen at #/screen update live beside the pledge form.
 */
import seedCards from "./seed-cards.json";
import seedThankYou from "./seed-thank-you.json";
import type { OrderedRecord, Pledge, StoryCard, ThankYouCard } from "../domain/types";
import type { CollectionRepository, PledgeRepository, Settings, SettingsRepository, Unsubscribe } from "./repositories";
import { DEFAULT_PRINT_SIZE_ID, isPrintSizeId } from "../config/print";
import { isEditionId } from "../config/editions";

/**
 * Storage keys. A key's version changes whenever its seed changes shape or
 * content, so every browser picks up the new approved set.
 * cards v2 (Session 1B): "A New Jamaica of Peace" removed, four new stories
 * added, "40 Years of Giving" renamed. Side-effect: edits saved under v1 in a
 * browser are not migrated (demo data only).
 * cards v3 + thankYou v2 (Session 1C, KI #32): seeds now hold Samantha's
 * 2026-10-19 edits, imported by libexec/import-backup.mjs (5 cards re-keyed,
 * order renumbered, 12 new photos). Side-effect: edits saved under the old
 * keys are not migrated; the only such edits known are the ones imported.
 */
const KEYS = {
    cards: "afj.tjwl.cards.v3",
    thankYou: "afj.tjwl.thankYou.v2",
    pledges: "afj.tjwl.pledges.v1",
    offline: "afj.tjwl.offlineTotal.v1",
    offlineCount: "afj.tjwl.offlineCount.v1",
    settings: "afj.tjwl.settings.v1",
} as const;

/** Reads JSON from storage, falling back when storage is empty, blocked or corrupt. */
function readJson<T>(key: string, fallback: T): T {
    try {
        const raw = window.localStorage.getItem(key);
        return raw === null ? fallback : (JSON.parse(raw) as T);
    } catch (error) {
        console.error(`[browserStore] Could not read "${key}"`, error);
        return fallback;
    }
}

/** Writes JSON to storage. Surfaces quota errors instead of swallowing them. */
function writeJson(key: string, value: unknown): void {
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        console.error(`[browserStore] Could not write "${key}"`, error);
        throw new Error("Browser storage is full or blocked. Use a smaller image, or clear demo data in Admin.");
    }
}

/**
 * Minimal observable. Emits on local writes and on writes from other tabs.
 * Cached snapshots keep React's useSyncExternalStore referentially stable.
 */
class Channel {
    private listeners = new Set<() => void>();

    constructor(private readonly keys: readonly string[]) {
        window.addEventListener("storage", (event) => {
            if (event.key === null || this.keys.includes(event.key)) this.emit();
        });
    }

    subscribe(listener: () => void): Unsubscribe {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    emit(): void {
        this.listeners.forEach((listener) => listener());
    }
}

/** One implementation for every ordered collection (DRY): story cards and thank-you cards. */
export class BrowserCollectionRepository<T extends OrderedRecord> implements CollectionRepository<T> {
    private readonly channel: Channel;
    private cache: T[] | null = null;

    constructor(private readonly key: string, private readonly seed: readonly T[]) {
        this.channel = new Channel([key]);
        this.channel.subscribe(() => (this.cache = null));
    }

    list(): T[] {
        this.cache ??= [...readJson<T[]>(this.key, [...this.seed])].sort((a, b) => a.order - b.order);
        return this.cache;
    }

    save(record: T): void {
        const others = this.list().filter((r) => r.id !== record.id);
        this.commit([...others, record]);
    }

    remove(id: string): void {
        this.commit(this.list().filter((r) => r.id !== id));
    }

    resetToSeed(): void {
        this.commit([...this.seed]);
    }

    subscribe(listener: () => void): Unsubscribe {
        return this.channel.subscribe(listener);
    }

    private commit(records: T[]): void {
        writeJson(this.key, records);
        this.cache = null;
        this.channel.emit();
    }
}

export const createBrowserCardRepository = () =>
    new BrowserCollectionRepository<StoryCard>(KEYS.cards, seedCards as StoryCard[]);

export const createBrowserThankYouRepository = () =>
    new BrowserCollectionRepository<ThankYouCard>(KEYS.thankYou, seedThankYou as ThankYouCard[]);

export class BrowserPledgeRepository implements PledgeRepository {
    private channel = new Channel([KEYS.pledges, KEYS.offline, KEYS.offlineCount]);
    private cache: Pledge[] | null = null;

    constructor() {
        this.channel.subscribe(() => (this.cache = null));
    }

    list(): Pledge[] {
        this.cache ??= readJson<Pledge[]>(KEYS.pledges, []);
        return this.cache;
    }

    add(pledge: Pledge): void {
        this.commit([pledge, ...this.list()]);
    }

    remove(id: string): void {
        this.commit(this.list().filter((p) => p.id !== id));
    }

    clear(): void {
        this.commit([]);
    }

    getOfflineTotal(): number {
        return readJson<number>(KEYS.offline, 0);
    }

    setOfflineTotal(amount: number): void {
        writeJson(KEYS.offline, Math.max(0, Math.round(amount)));
        this.channel.emit();
    }

    getOfflineCount(): number {
        return readJson<number>(KEYS.offlineCount, 0);
    }

    setOfflineCount(count: number): void {
        writeJson(KEYS.offlineCount, Math.max(0, Math.round(count)));
        this.channel.emit();
    }

    subscribe(listener: () => void): Unsubscribe {
        return this.channel.subscribe(listener);
    }

    private commit(pledges: Pledge[]): void {
        writeJson(KEYS.pledges, pledges);
        this.cache = null;
        this.channel.emit();
    }
}

const DEFAULT_SETTINGS: Settings = { editionOverride: null, printSize: DEFAULT_PRINT_SIZE_ID };

export class BrowserSettingsRepository implements SettingsRepository {
    private channel = new Channel([KEYS.settings]);
    private cache: Settings | null = null;

    constructor() {
        this.channel.subscribe(() => (this.cache = null));
    }

    /** Validates stored values, so a hand-edited or stale entry can never select an unknown edition or size. */
    get(): Settings {
        if (this.cache) return this.cache;
        const raw = readJson<Partial<Settings>>(KEYS.settings, {});
        this.cache = {
            editionOverride: isEditionId(raw.editionOverride) ? raw.editionOverride : DEFAULT_SETTINGS.editionOverride,
            printSize: isPrintSizeId(raw.printSize) ? raw.printSize : DEFAULT_SETTINGS.printSize,
        };
        return this.cache;
    }

    update(patch: Partial<Settings>): void {
        writeJson(KEYS.settings, { ...this.get(), ...patch });
        this.cache = null;
        this.channel.emit();
    }

    subscribe(listener: () => void): Unsubscribe {
        return this.channel.subscribe(listener);
    }
}
